import React, { useState, useEffect, useRef } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './SizeTest.css';

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru';

const SIZE_LABELS = { 1: 'маленький', 2: 'средний', 3: 'большой' };

const describeOrderMistake = (order) => {
  const correct = [1, 2, 3];
  if (order[0] === correct[0] && order[1] === correct[1] && order[2] === correct[2]) return null;

  const matchesAtPosition = order.map((v, i) => v === correct[i]);
  const mismatchCount = matchesAtPosition.filter(m => !m).length;

  if (mismatchCount === 2) {
    const swapped = order
      .map((v, i) => (!matchesAtPosition[i] ? v : null))
      .filter(v => v !== null);
    return `перепутал местами ${SIZE_LABELS[swapped[0]]} и ${SIZE_LABELS[swapped[1]]} предметы`;
  }

  return `расставил в порядке «${order.map(v => SIZE_LABELS[v]).join(' → ')}» вместо «маленький → средний → большой»`;
};

// Надежный алгоритм перемешивания (Fisher-Yates)
const shuffleArray = (arr) => {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const SizeTest = ({ onBack, onComplete }) => {
  const { play: playInstruction } = useInstructionAudio();
  const isCompleteRef = useRef(false);
  const exercises = [
    {
      id: 'pyramid',
      label: 'пирамидка',
      items: [
        { id: 1, size: 'маленькая', url: `${S3_BASE_URL}/pyramid_1.png`, order: 1 },
        { id: 2, size: 'средняя', url: `${S3_BASE_URL}/pyramid_2.png`, order: 2 },
        { id: 3, size: 'большая', url: `${S3_BASE_URL}/pyramid_3.png`, order: 3 },
      ],
    },
    {
      id: 'balls',
      label: 'мячи',
      items: [
        { id: 1, size: 'маленький', url: `${S3_BASE_URL}/ball_1.png`, order: 1 },
        { id: 2, size: 'средний', url: `${S3_BASE_URL}/ball_2.png`, order: 2 },
        { id: 3, size: 'большой', url: `${S3_BASE_URL}/ball_3.png`, order: 3 },
      ],
    },
  ];

  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [items, setItems] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [exerciseLog, setExerciseLog] = useState([]);
  const [showResult, setShowResult] = useState(false);

  const currentExercise = exercises[currentExerciseIndex];
  const totalExercises = exercises.length;

  useEffect(() => {
    if (currentExercise) {
      let shuffled = shuffleArray(currentExercise.items);
      
      // Гарантируем, что карточки НЕ стоят в правильном порядке изначально
      while (shuffled.every((item, index) => item.order === index + 1)) {
        shuffled = shuffleArray(currentExercise.items);
      }

      setItems(shuffled);
      setIsComplete(false);
      isCompleteRef.current = false;
      setSelectedIndex(null);
      setDragIndex(null);
    }
  }, [currentExerciseIndex]);

  const swapItems = (index1, index2) => {
    const newItems = [...items];
    [newItems[index1], newItems[index2]] = [newItems[index2], newItems[index1]];
    setItems(newItems);
  };

  useEffect(() => {
    if (currentExercise) {
      playInstruction('Расставь по порядку: от маленького к большому');
    }
  }, [currentExerciseIndex]);

  const handleCardClick = (clickedIndex) => {
    if (isComplete) return;
    if (selectedIndex === null) {
      setSelectedIndex(clickedIndex);
    } else if (selectedIndex === clickedIndex) {
      setSelectedIndex(null);
    } else {
      swapItems(selectedIndex, clickedIndex);
      setSelectedIndex(null);
    }
  };

  const handleDragStart = (e, index) => {
    if (isComplete) return;
    setSelectedIndex(null);
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      return;
    }
    swapItems(dragIndex, targetIndex);
    setDragIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  const handleOk = () => {
    if (isCompleteRef.current) return;
    
    const currentOrder = items.map(item => item.order);
    const isCorrect = currentOrder[0] === 1 && currentOrder[1] === 2 && currentOrder[2] === 3;
    const mistake = isCorrect ? null : describeOrderMistake(currentOrder);
    
    setAnswers([...answers, isCorrect]);
    setExerciseLog(prev => [...prev, {
      id: currentExercise.id,
      label: currentExercise.label,
      correct: isCorrect,
      order: currentOrder,
      mistake,
    }]);
    
    setIsComplete(true);
    isCompleteRef.current = true;
    setTimeout(() => {
      if (currentExerciseIndex < totalExercises - 1) {
        setCurrentExerciseIndex(currentExerciseIndex + 1);
      } else {
        setShowResult(true);
      }
    }, 500);
  };

  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        const correctCount = answers.filter(a => a === true).length;
        const score = answers.length > 0 ? Math.round((correctCount / answers.length) * 100) : 0;
        
        if (onComplete) {
          onComplete({
            testId: 'SizeTest',
            score,
            details: {
              total: totalExercises,
              correct: correctCount,
              exercises: exerciseLog,
            },
          });
        } else {
          onBack();
        }
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showResult]);

  if (showResult) {
    const correctCount = answers.filter(a => a === true).length;
    return (
      <ModuleTemplate 
        progress={answers} 
        totalQuestions={totalExercises} 
        forceStop={true}
        onComplete={onComplete || onBack}
      >
        <div className="result-content">
          <h2 className="result-title">🎉 Модуль завершён!</h2>
          <div className="result-score">
            <span className="score-number">{correctCount}</span>
            <span className="score-total"> / {totalExercises}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  return (
    <ModuleTemplate 
      progress={answers} 
      totalQuestions={totalExercises} 
      forceStop={true}
      onComplete={onComplete || onBack}
      extraContent="Расставь по порядку: от маленького к большому"
      extraButtons={
        <button 
          className="size-confirm-btn" 
          onClick={handleOk} 
          disabled={isComplete}
        >
          ОК
        </button>
      }
    >
      <div className="size-items-row">
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`size-item-card ${selectedIndex === index ? 'selected-card' : ''} ${dragIndex === index ? 'dragging' : ''}`}
            onClick={() => handleCardClick(index)}
            draggable={!isComplete}
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
          >
            <img src={item.url} alt={item.size} className="size-item-img" />
          </div>
        ))}
      </div>
    </ModuleTemplate>
  );
};

export default SizeTest;